'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const SUBJECTS = [
  'General enquiry',
  'Help with a listing',
  'Membership or billing',
  'Report a problem',
  'Advertising',
  'Something else',
];

export default function ContactScreen() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: SUBJECTS[0],
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: string, value: string) => setForm((f) => ({ ...f, [field]: value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Your message could not be sent.');
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your message could not be sent.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <main className="max-w-screen-md mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
          <Icon name="CheckCircleIcon" size={34} variant="solid" className="text-green-600" />
        </div>
        <h1 className="text-2xl font-extrabold text-foreground mb-3">Message received</h1>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          Thanks — we&apos;ve got your message and will come back to you as soon as we can.
        </p>
        <Link href="/" className="btn-primary">Back to the marketplace</Link>
      </main>
    );
  }

  return (
    <main className="max-w-screen-xl mx-auto px-4 lg:px-8 py-10">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2">
          <h1 className="text-3xl font-extrabold text-foreground mb-3">Contact us</h1>
          <p className="text-muted-foreground leading-relaxed mb-6">
            Questions about a listing, your membership, or something not working as it should? Send
            us a message and we&apos;ll get back to you.
          </p>

          <div className="space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <Icon name="ClockIcon" size={18} className="text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-foreground">Response time</p>
                <p className="text-muted-foreground">
                  We aim to answer within one working day.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Icon name="ChatBubbleLeftRightIcon" size={18} className="text-primary shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-foreground">Buying or selling?</p>
                <p className="text-muted-foreground">
                  Messages about a particular listing are quickest through the{' '}
                  <Link href="/messaging-centre" className="text-primary hover:underline">
                    messaging centre
                  </Link>{' '}
                  — the seller gets them directly.
                </p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-3 card">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="form-label" htmlFor="ct-name">Your name</label>
              <input
                id="ct-name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                required
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label" htmlFor="ct-email">Email</label>
              <input
                id="ct-email"
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                required
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label" htmlFor="ct-phone">
                Phone <span className="font-normal text-muted-foreground">(optional)</span>
              </label>
              <input
                id="ct-phone"
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label" htmlFor="ct-subject">What&apos;s it about?</label>
              <select
                id="ct-subject"
                value={form.subject}
                onChange={(e) => set('subject', e.target.value)}
                className="form-input"
              >
                {SUBJECTS.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="form-label" htmlFor="ct-message">Message</label>
            <textarea
              id="ct-message"
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
              required
              rows={7}
              className="form-input"
            />
          </div>

          {error && (
            <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-4" role="alert">
              {error}
            </p>
          )}

          <button type="submit" disabled={sending} className="btn-primary w-full justify-center py-3 mt-5 disabled:opacity-60">
            <Icon name="PaperAirplaneIcon" size={18} />
            {sending ? 'Sending…' : 'Send message'}
          </button>
        </form>
      </div>
    </main>
  );
}
